import { format } from "date-fns";

import {
  DEGREE_OF_SNR_LOSS_TYPES,
  HEARING_LOSS_LEVEL,
  HEARING_LOSS_SHAPE,
  HEARING_LOSS_TYPE,
  TYMPANOMETRY_TYPES
} from "constants/audiology";
import { getFullName, isBlank } from "utils/common";

import { PATIENT_LAST_REPORT_OPTIONS } from "../merge-tags-options/patient-last-report.utils";
import {
  formatAcousticReflex,
  formatAcousticReflexDecay,
  formatAirConductionThresholds,
  formatBoneConductionThresholds,
  formatCustomTests,
  formatFullAudiogram,
  formatIcd10Codes,
  formatOaeResults,
  formatOaeType,
  formatPta,
  formatQuickSinMethod,
  formatSrt,
  formatSrtMasking,
  formatTympanometryAdmittance,
  formatTympanometryEcv,
  formatTympanometryPressure,
  formatWrsMasking
} from "./patient-report-clinical.utils";
import { MergeTagsReplaceHandler } from "./merge-tags-values";

export const PATIENT_LAST_REPORT_VALUES_VALUES: MergeTagsReplaceHandler = {
  [PATIENT_LAST_REPORT_OPTIONS.APPOINTMENT_DATE_TIME]: ({ data }) => {
    if (!data.lastReport?.appointment?.startDate) return null;

    return format(
      new Date(data.lastReport?.appointment.startDate),
      "EEEE MMMM dd, yyyy, 'at' h:mm aa"
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.APPOINTMENT_TYPE]: ({ data }) => {
    if (!data.lastReport?.appointment) return null;

    return data.lastReport.appointment.appointmentType?.name ?? null;
  },

  [PATIENT_LAST_REPORT_OPTIONS.LEFT_HEARING_LOSS]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_left
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_HEARING_LOSS]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_right
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_WRS_PRESENTATION_LEVEL_LOSS]: ({
    data
  }) => {
    const percentCorrect =
      data.lastReport?.wordRecognitionReport?.leftEar?.percentCorrect;

    const dbhl = data.lastReport?.wordRecognitionReport?.leftEar?.dbhl;

    if (isBlank(percentCorrect) && isBlank(dbhl)) return null;

    if (isBlank(dbhl)) {
      return `${percentCorrect}%`;
    } else if (isBlank(percentCorrect)) {
      return `${dbhl} dBHL`;
    }

    return `${percentCorrect}% at ${dbhl} dBHL`;
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_WRS_PRESENTATION_LEVEL_LOSS]: ({
    data
  }) => {
    const percentCorrect =
      data.lastReport?.wordRecognitionReport?.rightEar?.percentCorrect;

    const dbhl = data.lastReport?.wordRecognitionReport?.rightEar?.dbhl;

    if (isBlank(percentCorrect) && isBlank(dbhl)) return null;

    if (isBlank(dbhl)) {
      return `${percentCorrect}%`;
    } else if (isBlank(percentCorrect)) {
      return `${dbhl} dBHL`;
    }

    return `${percentCorrect}% at ${dbhl} dBHL`;
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_QUICKSIN_AND_SNR_LOSS]: ({ data }) => {
    const snrLoss = data.lastReport?.speechInNoiseReport?.leftEar?.snrLoss;
    const snrLossDegree =
      data.lastReport?.speechInNoiseReport?.leftEar?.snrLossDegree;

    if (isBlank(snrLoss) && isBlank(snrLossDegree)) return null;

    if (isBlank(snrLossDegree)) {
      return `${snrLoss} dB`;
    } else if (isBlank(snrLoss)) {
      return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss`;
    }

    return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss at ${snrLoss} dB`;
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_QUICKSIN_AND_SNR_LOSS]: ({ data }) => {
    const snrLoss = data.lastReport?.speechInNoiseReport?.rightEar?.snrLoss;
    const snrLossDegree =
      data.lastReport?.speechInNoiseReport?.rightEar?.snrLossDegree;

    if (isBlank(snrLoss) && isBlank(snrLossDegree)) return null;

    if (isBlank(snrLossDegree)) {
      return `${snrLoss} dB`;
    } else if (isBlank(snrLoss)) {
      return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss`;
    }

    return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss at ${snrLoss} dB`;
  },
  [PATIENT_LAST_REPORT_OPTIONS.BINAURAL_QUICKSIN_AND_SNR_LOSS]: ({ data }) => {
    const snrLoss = data.lastReport?.speechInNoiseReport?.binaural?.snrLoss;
    const snrLossDegree =
      data.lastReport?.speechInNoiseReport?.binaural?.snrLossDegree;

    if (isBlank(snrLoss) && isBlank(snrLossDegree)) return null;

    if (isBlank(snrLossDegree)) {
      return `${snrLoss} dB`;
    } else if (isBlank(snrLoss)) {
      return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss`;
    }

    return `${DEGREE_OF_SNR_LOSS_TYPES.getFromCode(snrLossDegree)} degree of SNR Loss at ${snrLoss} dB`;
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_TYMPANOGRAM_TYPE]: ({ data }) => {
    return TYMPANOMETRY_TYPES.getFromCode(
      data.lastReport?.tympanometryReport?.leftEar?.type
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_TYMPANOGRAM_TYPE]: ({ data }) => {
    return TYMPANOMETRY_TYPES.getFromCode(
      data.lastReport?.tympanometryReport?.rightEar?.type
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_HEARING_LOSS_LEVEL]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_left
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_HEARING_LOSS_LEVEL]: ({ data }) => {
    return HEARING_LOSS_LEVEL.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_right
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_HEARING_LOSS_TYPE]: ({ data }) => {
    return HEARING_LOSS_TYPE.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_type_left
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_HEARING_LOSS_TYPE]: ({ data }) => {
    return HEARING_LOSS_TYPE.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_type_right
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_HEARING_LOSS_SHAPE]: ({ data }) => {
    return HEARING_LOSS_SHAPE.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_shape_left
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_HEARING_LOSS_SHAPE]: ({ data }) => {
    return HEARING_LOSS_SHAPE.getFromCode(
      data.lastReport?.diagnosis?.hearing_loss_shape_right
    );
  },
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_AIR_CONDUCTION_THRESHOLDS]: ({ data }) =>
    formatAirConductionThresholds(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_AIR_CONDUCTION_THRESHOLDS]: ({ data }) =>
    formatAirConductionThresholds(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_BONE_CONDUCTION_THRESHOLDS]: ({ data }) =>
    formatBoneConductionThresholds(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_BONE_CONDUCTION_THRESHOLDS]: ({ data }) =>
    formatBoneConductionThresholds(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_PTA]: ({ data }) =>
    formatPta(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_PTA]: ({ data }) =>
    formatPta(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_SRT]: ({ data }) =>
    formatSrt(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_SRT]: ({ data }) =>
    formatSrt(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_SRT_MASKING]: ({ data }) =>
    formatSrtMasking(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_SRT_MASKING]: ({ data }) =>
    formatSrtMasking(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_WRS_MASKING]: ({ data }) =>
    formatWrsMasking(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_WRS_MASKING]: ({ data }) =>
    formatWrsMasking(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_TYMPANOMETRY_ADMITTANCE]: ({ data }) =>
    formatTympanometryAdmittance(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_TYMPANOMETRY_ADMITTANCE]: ({ data }) =>
    formatTympanometryAdmittance(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_TYMPANOMETRY_ECV]: ({ data }) =>
    formatTympanometryEcv(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_TYMPANOMETRY_ECV]: ({ data }) =>
    formatTympanometryEcv(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_TYMPANOMETRY_PRESSURE]: ({ data }) =>
    formatTympanometryPressure(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_TYMPANOMETRY_PRESSURE]: ({ data }) =>
    formatTympanometryPressure(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_ACOUSTIC_REFLEX_THRESHOLDS]: ({ data }) =>
    formatAcousticReflex(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_ACOUSTIC_REFLEX_THRESHOLDS]: ({ data }) =>
    formatAcousticReflex(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_ACOUSTIC_REFLEX_DECAY]: ({ data }) =>
    formatAcousticReflexDecay(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_ACOUSTIC_REFLEX_DECAY]: ({ data }) =>
    formatAcousticReflexDecay(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_QUICKSIN_METHOD]: ({ data }) =>
    formatQuickSinMethod(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_QUICKSIN_METHOD]: ({ data }) =>
    formatQuickSinMethod(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_OAE_TYPE]: ({ data }) =>
    formatOaeType(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_OAE_TYPE]: ({ data }) =>
    formatOaeType(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.LEFT_OAE_RESULTS]: ({ data }) =>
    formatOaeResults(data.lastReport, "L"),
  [PATIENT_LAST_REPORT_OPTIONS.RIGHT_OAE_RESULTS]: ({ data }) =>
    formatOaeResults(data.lastReport, "R"),
  [PATIENT_LAST_REPORT_OPTIONS.ICD10_CODES]: ({ data }) =>
    formatIcd10Codes(data.lastReport),
  [PATIENT_LAST_REPORT_OPTIONS.CUSTOM_TESTS]: ({ data }) =>
    formatCustomTests(data.lastReport),
  [PATIENT_LAST_REPORT_OPTIONS.FULL_AUDIOGRAM]: ({ data }) =>
    formatFullAudiogram(data.lastReport),
  [PATIENT_LAST_REPORT_OPTIONS.SUMMARY]: ({ data }) => {
    return data.lastReport?.summary;
  },
  [PATIENT_LAST_REPORT_OPTIONS.PROVIDER_NAME]: ({ data }) => {
    const staffMember = data.lastReport?.appointment?.staffMember;

    if (!staffMember) return null;

    return getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });
  },
  [PATIENT_LAST_REPORT_OPTIONS.PROVIDER_NAME_WITH_LICENSE]: ({ data }) => {
    const staffMember = data.lastReport?.appointment?.staffMember;

    if (!staffMember) return null;

    const providerName = getFullName({
      firstName: staffMember?.first_name || "",
      lastName: staffMember?.last_name || "",
      title: staffMember?.title,
      suffix: staffMember?.suffix || ""
    });

    const licenseNumber =
      staffMember?.resolved_license_number || staffMember?.license_number || "";

    return licenseNumber
      ? `${providerName}, License #${licenseNumber}`
      : providerName;
  },
  [PATIENT_LAST_REPORT_OPTIONS.PROVIDER_LICENSE_NUMBER]: ({ data }) => {
    const staffMember = data.lastReport?.appointment?.staffMember;

    if (!staffMember) return null;

    return (
      staffMember?.resolved_license_number || staffMember?.license_number || ""
    );
  }
};
